import React from 'react';

const NOTION_TEXT_COLORS = {
  gray: 'text-grayscale-gray06',
  brown: 'text-[#8B5C3E]',
  orange: 'text-[#D9730D]',
  yellow: 'text-[#CB912F]',
  green: 'text-green-deep02',
  blue: 'text-[#2F80ED]',
  purple: 'text-[#7B61FF]',
  pink: 'text-[#E255A1]',
  red: 'text-functional-failed03',
};

const NOTION_BG_COLORS = {
  gray_background: 'bg-grayscale-gray02',
  brown_background: 'bg-[#F4EEEE]',
  orange_background: 'bg-[#FBECDD]',
  yellow_background: 'bg-[#FBF3DB]',
  green_background: 'bg-green-light01',
  blue_background: 'bg-[#E7F3F8]',
  purple_background: 'bg-[#F1EEFB]',
  pink_background: 'bg-[#FBEEF3]',
  red_background: 'bg-[#FDEBEC]',
};

const colorClass = (color) => {
  if (!color || color === 'default') return '';
  if (color.endsWith('_background')) {
    return `${NOTION_BG_COLORS[color] ?? ''} rounded px-1`;
  }
  return NOTION_TEXT_COLORS[color] ?? '';
};

const RichTextChunk = ({ chunk }) => {
  const { annotations = {}, plain_text: plainText = '', href } = chunk;

  const content = plainText.split('\n').map((line, index, lines) => (
    <React.Fragment key={index}>
      {line}
      {index < lines.length - 1 && <br />}
    </React.Fragment>
  ));

  if (annotations.code) {
    return (
      <code className='rounded bg-grayscale-gray02 px-1.5 py-0.5 text-functional-failed03 typo-b13'>
        {content}
      </code>
    );
  }

  const className = [
    annotations.bold && 'font-bold',
    annotations.italic && 'italic',
    annotations.underline && 'underline underline-offset-2',
    annotations.strikethrough && 'line-through',
    colorClass(annotations.color),
  ]
    .filter(Boolean)
    .join(' ');

  const node = className ? (
    <span className={className}>{content}</span>
  ) : (
    content
  );

  if (href) {
    return (
      <a
        href={href}
        target='_blank'
        rel='noopener noreferrer'
        className='text-green-deep02 underline underline-offset-2 hover:text-green-deep03'
      >
        {node}
      </a>
    );
  }

  return <>{node}</>;
};

export const RichText = ({ richText = [] }) => (
  <>
    {richText.map((chunk, index) => (
      <RichTextChunk key={index} chunk={chunk} />
    ))}
  </>
);

const plainText = (richText = []) =>
  richText.map((chunk) => chunk.plain_text ?? '').join('');

const imageUrl = (node) => node?.external?.url ?? node?.file?.url ?? '';

const Caption = ({ caption = [] }) =>
  caption.length === 0 ? null : (
    <figcaption className='mt-2 text-center typo-b12 text-text-placeholder'>
      <RichText richText={caption} />
    </figcaption>
  );

const Block = ({ block }) => {
  const { type } = block;
  const value = block[type] ?? {};
  const children = block.children ?? [];

  switch (type) {
    case 'paragraph': {
      const isEmpty = !value.rich_text || value.rich_text.length === 0;

      if (isEmpty && children.length === 0) return <div className='h-4' />;
      if (isEmpty) return <BlockList blocks={children} />;

      return (
        <>
          <p className='typo-b14 tabletV:typo-b16 text-text-primary leading-[1.7] whitespace-pre-wrap break-words'>
            <RichText richText={value.rich_text} />
          </p>
          {children.length > 0 && (
            <div className='pl-4'>
              <BlockList blocks={children} />
            </div>
          )}
        </>
      );
    }

    case 'heading_1':
      return (
        <h2 className='mt-8 typo-title1 tabletV:typo-h6 text-text-primary'>
          <RichText richText={value.rich_text} />
        </h2>
      );

    case 'heading_2':
      return (
        <h3 className='mt-6 typo-b20Strong tabletV:typo-title1 text-text-primary'>
          <RichText richText={value.rich_text} />
        </h3>
      );

    case 'heading_3':
      return (
        <h4 className='mt-4 typo-b16Strong tabletV:typo-b20Strong text-text-primary'>
          <RichText richText={value.rich_text} />
        </h4>
      );

    case 'bulleted_list_item':
      return (
        <li className='typo-b14 tabletV:typo-b16 text-text-primary leading-[1.7] marker:text-text-placeholder'>
          <RichText richText={value.rich_text} />
          {children.length > 0 && <BlockList blocks={children} />}
        </li>
      );

    case 'numbered_list_item':
      return (
        <li className='typo-b14 tabletV:typo-b16 text-text-primary leading-[1.7] marker:text-text-placeholder'>
          <RichText richText={value.rich_text} />
          {children.length > 0 && <BlockList blocks={children} />}
        </li>
      );

    case 'to_do':
      return (
        <div className='flex items-start gap-2'>
          <input
            type='checkbox'
            checked={Boolean(value.checked)}
            readOnly
            className='mt-1 h-4 w-4 accent-green-deep02'
          />
          <span
            className={`typo-b14 tabletV:typo-b16 leading-[1.7] ${
              value.checked
                ? 'text-text-placeholder line-through'
                : 'text-text-primary'
            }`}
          >
            <RichText richText={value.rich_text} />
            {children.length > 0 && (
              <div className='pl-1'>
                <BlockList blocks={children} />
              </div>
            )}
          </span>
        </div>
      );

    case 'quote':
      return (
        <blockquote className='border-l-[3px] border-green-light03 pl-4 typo-b14 tabletV:typo-b16 text-text-secondary leading-[1.7]'>
          <RichText richText={value.rich_text} />
          {children.length > 0 && <BlockList blocks={children} />}
        </blockquote>
      );

    case 'callout':
      return (
        <div className='flex gap-3 rounded-[12px] bg-grayscale-gray01 p-4'>
          {value.icon?.emoji && (
            <span className='shrink-0 typo-b16'>{value.icon.emoji}</span>
          )}
          <div className='typo-b14 tabletV:typo-b16 text-text-primary leading-[1.7]'>
            <RichText richText={value.rich_text} />
            {children.length > 0 && <BlockList blocks={children} />}
          </div>
        </div>
      );

    case 'code':
      return (
        <figure>
          <pre className='overflow-x-auto rounded-[12px] bg-grayscale-gray09 p-4'>
            <code className='typo-b13 text-grayscale-gray00 whitespace-pre'>
              {plainText(value.rich_text)}
            </code>
          </pre>
          <Caption caption={value.caption} />
        </figure>
      );

    case 'image': {
      const src = imageUrl(value);
      if (!src) return null;
      return (
        <figure>
          <img
            src={src}
            alt={plainText(value.caption) || 'article image'}
            loading='lazy'
            className='mx-auto h-auto max-w-full rounded-[12px]'
          />
          <Caption caption={value.caption} />
        </figure>
      );
    }

    case 'video': {
      const src = imageUrl(value);
      if (!src) return null;
      const youtubeId = src.match(
        /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]{11})/,
      )?.[1];
      return (
        <figure className='aspect-video w-full overflow-hidden rounded-[12px]'>
          {youtubeId ? (
            <iframe
              src={`https://www.youtube.com/embed/${youtubeId}`}
              title='article video'
              allowFullScreen
              className='h-full w-full'
            />
          ) : (
            <video src={src} controls className='h-full w-full' />
          )}
        </figure>
      );
    }

    case 'bookmark':
    case 'embed': {
      const url = value.url ?? '';
      if (!url) return null;
      return (
        <a
          href={url}
          target='_blank'
          rel='noopener noreferrer'
          className='block rounded-[12px] border border-grayscale-gray02 p-4 typo-b13 text-text-secondary hover:border-green-light03'
        >
          {url}
        </a>
      );
    }

    case 'divider':
      return <hr className='border-t border-grayscale-gray02' />;

    case 'toggle':
      return (
        <details className='rounded-[12px] bg-grayscale-gray01 p-4'>
          <summary className='cursor-pointer typo-b14Strong tabletV:typo-b16Strong text-text-primary'>
            <RichText richText={value.rich_text} />
          </summary>
          {children.length > 0 && (
            <div className='mt-3'>
              <BlockList blocks={children} />
            </div>
          )}
        </details>
      );

    case 'table':
      return (
        <div className='overflow-x-auto'>
          <table className='w-full border-collapse text-left'>
            <tbody>
              {children.map((row, rowIndex) => {
                const cells = row?.table_row?.cells ?? [];
                const isHeader = value.has_column_header && rowIndex === 0;
                return (
                  <tr key={row.id ?? rowIndex}>
                    {cells.map((cell, cellIndex) => {
                      const Cell = isHeader ? 'th' : 'td';
                      return (
                        <Cell
                          key={cellIndex}
                          className={`border border-grayscale-gray02 px-3 py-2 typo-b13 tabletV:typo-b14 ${
                            isHeader
                              ? 'bg-grayscale-gray01 text-text-primary font-bold'
                              : 'text-text-secondary'
                          }`}
                        >
                          <RichText richText={cell} />
                        </Cell>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );

    case 'column_list':
      return (
        <div className='flex flex-col gap-4 tabletV:flex-row'>
          {children.map((column) => (
            <div key={column.id} className='flex-1'>
              <BlockList blocks={column.children ?? []} />
            </div>
          ))}
        </div>
      );

    default:
      if (Array.isArray(value.rich_text) && value.rich_text.length > 0) {
        return (
          <p className='typo-b14 tabletV:typo-b16 text-text-primary leading-[1.7]'>
            <RichText richText={value.rich_text} />
          </p>
        );
      }
      return null;
  }
};

const BlockList = ({ blocks = [] }) => {
  const groups = [];

  blocks.forEach((block) => {
    const last = groups[groups.length - 1];
    const isBullet = block.type === 'bulleted_list_item';
    const isNumbered = block.type === 'numbered_list_item';

    if ((isBullet || isNumbered) && last?.type === block.type) {
      last.items.push(block);
      return;
    }
    if (isBullet || isNumbered) {
      groups.push({ type: block.type, items: [block] });
      return;
    }
    groups.push({ type: 'block', items: [block] });
  });

  return (
    <div className='flex flex-col gap-4'>
      {groups.map((group, index) => {
        if (group.type === 'bulleted_list_item') {
          return (
            <ul key={index} className='flex list-disc flex-col gap-2 pl-5'>
              {group.items.map((block) => (
                <Block key={block.id} block={block} />
              ))}
            </ul>
          );
        }
        if (group.type === 'numbered_list_item') {
          return (
            <ol key={index} className='flex list-decimal flex-col gap-2 pl-5'>
              {group.items.map((block) => (
                <Block key={block.id} block={block} />
              ))}
            </ol>
          );
        }
        const block = group.items[0];
        return <Block key={block.id ?? index} block={block} />;
      })}
    </div>
  );
};

const NotionRenderer = ({ blocks = [], className = '' }) => {
  if (!blocks || blocks.length === 0) return null;
  return (
    <div className={className}>
      <BlockList blocks={blocks} />
    </div>
  );
};

export default NotionRenderer;
